import React from 'react';
import { connect } from 'react-redux';
import { KeyLicenseTypes } from '../common/keyLicenseTypes';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import TemporaryLicenseTable from '../components/temporaryLicenseTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import * as temporaryLicensesActions from '../store/temporaryLicensesSlice';
import { temporaryLicensesSelectors } from '../store/temporaryLicensesSlice';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import { showModalMessage } from '../store/modalsSlice';
import KeyLicenseEditDialog from './keyLicenseEditDialog';
import ModalMessage from './modalMessage';
import DeactivateLicConfirmDialog from './deactivateLicConfirmDialog';
import TemporaryLicenseHistoryDialog from './temporaryLicenseHistoryDialog';
import OnlineLicenseActivatingDialog from './onlineLicenseActivationDialog';
import './common.css';
import setTitleToFilters from '../common/filtersTitles';
import { unwrapResult } from '@reduxjs/toolkit';
import extractSearchParams from '../common/extractSearchParams';
import * as filterValuesActions from '../store/userConfigs/filterValuesSlice';

const thatObjectName = 'temporaryLicenses';

class TemporaryLicenseContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showTempLicEditDialog: false,
      temporaryLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Create,
      showDeactivateLicConfirmDialog: false,
      showTemporaryLicenseHistoryDialog: false,
      showLicenseActivationDialog: false
    };
  }

  componentDidMount() {
    setTitleToFilters();

    const licenseFilterValue = extractSearchParams(this.props.requestData.location.search, 'license.key');
    if (licenseFilterValue) this.props.setFilterValue('key', licenseFilterValue);

    this.props.fetchTemporaryLicenses();
    this.props.fetchProducts();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Online temporary licenses</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderTempLicEditDialog()}
          {this.renderModalMessage()}
          {this.renderDeactivateTempLicsConfirmDialog()}
          {this.renderTempLicHistoryDialog()}
          {this.renderLicenseActivationDialog()}
        </div>
      </OverlayLoader>
    );
  }

  renderButtons = () => (
    <TooltipButtonGroup className="containerButtonGroup">
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.refreshTemporaryLicenses} />
      <TooltipElement type="button" text="Create license" icon="plus"
                      onClick={this.createTemporaryLicense} displayOnlyForWriter />
      <TooltipElement type="button" text="Edit license" icon="edit" onClick={this.editTemporaryLicense}
                      disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Deactivate license" icon="off" onClick={this.deactivateTempLic}
                      disabled={this.isDeactivateButtonDisabled()} displayOnlyForWriter/>
      <TooltipElement type="button" text="Show license history" icon="list-alt" onClick={this.showLicenseHistory}
                      disabled={this.isHistoryButtonDisabled()} />
      <TooltipElement type="button" text="Download license" icon="download" onClick={this.downloadTemporaryLicense}
                      disabled={this.isDownloadButtonDisabled()} displayOnlyForWriter />
    </TooltipButtonGroup>
  );

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addTemporaryLicenseToFavorites,
    removeFromFavorites: this.props.removeTemporaryLicenseFromFavorites,
  });

  renderContent = () => {
    if (this.props.temporaryLicensesFetchingError) {
      return this.renderError(this.props.temporaryLicensesFetchingError.toString());
    }
    return <React.Fragment>
      {this.props.productsState.error ? this.renderError(this.props.productsState.error.toString()) : null}
      <TableSelectionManager
        items={this.props.temporaryLicenses}
        objectName={thatObjectName}
      >
        <TemporaryLicenseTable
          temporaryLicenses={this.props.temporaryLicenses}
          products={this.props.productsState.data}
          actions={this.getActionsForFavorites()}
          requestData={this.props.requestData}
        />
      </TableSelectionManager>
    </React.Fragment>;
  };

  renderError = message => (
    <p className="text-danger">Error: {message}</p>
  );

  renderTempLicEditDialog = () => (
    <KeyLicenseEditDialog
      temporalType={KeyLicenseTypes.OnlineTemporary}
      mode={this.state.temporaryLicenseEditDialogMode}
      onClose={this.closeTempLicEditDialog}
      show={this.state.showTempLicEditDialog}
      licKeyType={this.getSelectedLicenseFieldValue('type.code', '')}
      selectedFeatures={
        this.getSelectedLicenseFieldValue('features', [])
          .filter(feature => feature.expirationDate === null)
          .map(feature => feature.id)
      }
      licenseId={this.getSelectedLicenseFieldValue('id')}
      productName={this.getSelectedLicenseFieldValue('productName')}
      clientId={this.getSelectedLicenseFieldValue('client.id')}
      clientName={this.getSelectedLicenseFieldValue('client.fullName')}
      generationDate={this.getSelectedLicenseFieldValue('generationDateTime')}
      term={this.getSelectedLicenseFieldValue('term')}
    />
  );

  renderModalMessage = () => <ModalMessage />;

  refreshTemporaryLicenses = () => {
    this.props.fetchTemporaryLicenses();
  };

  createTemporaryLicense = () => {
    this.setState({
      temporaryLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Create,
      showTempLicEditDialog: true
    });
  };

  editTemporaryLicense = () => {
    this.setState({
      temporaryLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Edit,
      showTempLicEditDialog: true
    });
  };

  closeTempLicEditDialog = () => {
    this.setState({ showTempLicEditDialog: false });
  };

  renderDeactivateTempLicsConfirmDialog = () => (
    <DeactivateLicConfirmDialog
      temporalType={KeyLicenseTypes.OnlineTemporary}
      show={this.state.showDeactivateLicConfirmDialog}
      onClose={this.closeDeactivateLicConfirmDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
    />
  );

  deactivateTempLic = () => {
    this.setState({ showDeactivateLicConfirmDialog: true });
  };

  closeDeactivateLicConfirmDialog = () => {
    this.setState({ showDeactivateLicConfirmDialog: false });
  };

  renderTempLicHistoryDialog = () => (
    <TemporaryLicenseHistoryDialog
      show={this.state.showTemporaryLicenseHistoryDialog}
      onClose={this.closeTempLicHistoryDialog}
      id={this.getSelectedLicenseFieldValue('id')}
      licenseKey={this.getSelectedLicenseFieldValue('key')}
    />
  );

  showLicenseHistory = () => {
    this.setState({ showTemporaryLicenseHistoryDialog: true });
  };

  closeTempLicHistoryDialog = () => {
    this.setState({ showTemporaryLicenseHistoryDialog: false });
  };

  showLicenseActivationDialog = () => {
    this.setState({ showLicenseActivationDialog: true });
  };

  closeLicenseActivationDialog = () => {
    this.setState({ showLicenseActivationDialog: false });
  };

  renderLicenseActivationDialog = () => (
    <OnlineLicenseActivatingDialog
      show={this.state.showLicenseActivationDialog}
      onClose={this.closeLicenseActivationDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
      onSuccess={() => this.props.downloadLicense({
        licenseId: this.getSelectedLicenseFieldValue('id')
      })}
      licenseType={KeyLicenseTypes.OnlineTemporary}
    />
  );

  downloadTemporaryLicense = () => {
    let license = this.props.selectedLicenses[0];
    if (!license)
      return;
    if (license.device === null) {
      this.showLicenseActivationDialog();
      return;
    }
    this.props.downloadLicense({ licenseId: license.id })
      .then(unwrapResult)
      .catch(() => {
        this.props.showModalMessage("Error", this.props.downloadingError.toString());
      });
  };

  isLoading = () => this.props.isTemporaryLicensesFetching ||
    this.props.isTemporaryLicensesCreating ||
    this.props.isTemporaryLicenseUpdating ||
    this.props.productsState.isFetching ||
    this.props.isLicenseAssignmentInProcess;

  getLoaderMessage = () => {
    if (this.props.isTemporaryLicensesFetching) {
      return "Temporary licenses loading...";
    }
    if (this.props.isTemporaryLicensesCreating) {
      return "License key generating...";
    }
    if (this.props.isTemporaryLicenseUpdating) {
      return "Temporary license updating...";
    }
    if (this.props.productsState.isFetching) {
      return "Product names loading...";
    }
    if (this.props.isLicenseAssignmentInProcess) {
      return "License assignment...";
    }
  };

  isEditButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1) {
      return true;
    }
    let lic = this.props.selectedLicenses[0];
    return lic === null || lic.type === null || lic.type.deprecated;
  };

  isDeactivateButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1) {
      return true;
    }
    let temporaryLicense = this.props.selectedLicenses[0];
    return !temporaryLicense ||
      temporaryLicense.device === null;
  };

  isHistoryButtonDisabled = () => this.props.selectedLicenses.length !== 1;

  isDownloadButtonDisabled = () => this.props.selectedLicenses.length !== 1;

  getSelectedLicenseFieldValue = (fieldName, defaultValue = null) => {
    const license = this.props.selectedLicenses[0];
    if (!license) return defaultValue;
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], license);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };
}

const mapStateToProps = (state) => ({
  isTemporaryLicensesFetching: temporaryLicensesSelectors.isFetching(state),
  isTemporaryLicensesCreating: temporaryLicensesSelectors.isCreating(state),
  isTemporaryLicenseUpdating: temporaryLicensesSelectors.isUpdating(state),
  isLicenseAssignmentInProcess: temporaryLicensesSelectors.isAssignmentInProcess(state),

  temporaryLicenses: temporaryLicensesSelectors.selectAll(state),

  productsState: objectDataSelectors.onlineLicenseProductsState(state),

  temporaryLicensesFetchingError: temporaryLicensesSelectors.fetchingError(state),
  downloadingError: temporaryLicensesSelectors.downloadError(state),

  selectedLicenses: temporaryLicensesSelectors.selectedLicenses(state),
});

const actionCreators = {
  fetchTemporaryLicenses: temporaryLicensesActions.fetchAll,
  fetchProducts: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.onlineLicenseProducts }),
  downloadLicense: temporaryLicensesActions.download,
  showModalMessage: (title, text) => showModalMessage({ title, text }),
  setFilterValue: (filterName, value) => filterValuesActions.setFilterValue(thatObjectName, filterName, value),
  addTemporaryLicenseToFavorites: temporaryLicensesActions.addToFavorites,
  removeTemporaryLicenseFromFavorites: temporaryLicensesActions.removeFromFavorites,
};

export default connect(mapStateToProps, actionCreators)(TemporaryLicenseContainer);



// WEBPACK FOOTER //
// ./src/containers/temporaryLicensesContainer.js